import { Component } from "react";
import type { ReactElement } from "react";

import classNames from "classnames";
import { merge } from "lodash";

import {
    Autocomplete,
    AutosizeInput,
    BasicModal,
    Button,
    DotsLoader,
    ListItemWebsite,
    PlainTooltip,
} from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { isValidURL } from "app/pages/workspace/common/workspacesUtils";
import { AssetsService } from "app/services/AssetsService";
import { autoCompleteService } from "app/services/autocomplete.service";
import type { IAutoCompleteSiteItem, ICompanyItem } from "app/@types/autocomplete";
import WithTrack, { type track as TrackFn } from "pro-features/components/WithTrack/src/WithTrack";
import { PrimaryBoxTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";

import CompanyListItem from "./CompanyListItem/CompanyListItem";
import { defaultErrors, LIST_ID_OVERVIEW } from "./constants";
import {
    defaultStyle,
    websiteModalDefaultStyle,
    AutosizeTooltipAnchor,
    EditOpportuntiesModalTitle,
    Footer,
    SelectedKeysContainer,
    StyledButtonsGroup,
    StyledLimitMessage,
    StyledProTipBanner,
    StyledQuotaContainer,
    StyledWebsitesContainer,
    WebsiteModalContainer,
    WebsiteModalHeaderContainer,
    WebsitesModalAutoComplete,
} from "./styles";
import type {
    CompanyGroup,
    CompanyGroups,
    DomainInfo,
    IWebsiteItem,
    IWebsitesModal,
    WebsitesModalState,
} from "./types";

const getDomain = (url: string): string | undefined => {
    const domainRegex = /^(?:https?\:\/\/)?(?:www\.)?([^\/?#\\]+)(?:[\/?#\\]|$)/i;
    const match = url.match(domainRegex);
    return match?.[1]?.toLowerCase();
};

export class WebsitesModal extends Component<IWebsitesModal, WebsitesModalState> {
    public static defaultProps = {
        styles: defaultStyle,
        errors: defaultErrors,
        title: "",
    };

    private track: TrackFn;

    constructor(props: IWebsitesModal) {
        super(props);

        const userClosedProTipBanner = JSON.parse(
            localStorage.getItem("websites.modal.pro_tip_banner_shown") as string,
        );

        this.state = {
            showProTipBanner: userClosedProTipBanner !== true,
            activeListId: props.listName,
            listName: props.listName ?? "",
            errorMsg: null,
            isAutoCompleteLoading: false,
            country: this.props.country,
            selectedKeys: [],
            itemsToRender: [],
            isLoading: false,
            isPasteError: false,
            isValidationError: false,
            isManualError: false,
            isOverLimitError: false,
            isListNameTruncated: false,
        };
    }

    componentDidMount() {
        if (this.props?.quotaLimit) {
            this.setState({
                isOverLimit: this.props.quotaLimit <= (this.props?.spentQuota ?? 0) + this.state?.selectedKeys.length,
            });
        }
    }

    public componentDidUpdate(prevProps: Readonly<IWebsitesModal>): void {
        if (this.props.listName !== prevProps.listName) {
            this.setState({ listName: this.props.listName ?? "" });
            this.setState({ activeListId: this.props.activeListId });
        }

        // Reset listName in state after closing modal
        if (this.props.isOpen !== prevProps.isOpen && !this.props.isOpen) {
            this.setState({ listName: this.props.listName ?? "" });
        }
    }

    onCloseProTipBanner = () => {
        this.setState({ showProTipBanner: false });
        localStorage.setItem("websites.modal.pro_tip_banner_shown", JSON.stringify(true));
    };

    private getCompanyGroups = (keys: DomainInfo[]): CompanyGroups => {
        return keys.reduce((acc: CompanyGroups, domainItem: DomainInfo) => {
            if (!acc[domainItem.companyInfo.companyName]) {
                acc[domainItem.companyInfo.companyName] = {
                    companyInfo: domainItem.companyInfo,
                    icon: domainItem.icon,
                    domains: [],
                };
            }

            acc[domainItem.companyInfo.companyName].domains.push(domainItem.domain);

            return acc;
        }, {});
    };

    renderErrorLimitMessage = () => {
        const { isPasteError, isValidationError, isManualError, isOverLimitError } = this.state;

        let error = "";

        if (isValidationError) {
            error = "workspace.websitemodal.error.validation.message";
        }
        if (isPasteError) {
            error = "workspace.websitemodal.quota.error.paste.message";
        }
        if (isManualError) {
            error = "workspace.websitemodal.quota.error.manual.message";
        }
        if (isOverLimitError) {
            error = "workspace.websitemodal.quota.error.limit.message";
        }

        if (error) {
            return <StyledLimitMessage>{translate(error, { limit: this.props.inputLimit })}</StyledLimitMessage>;
        }
    };

    public render() {
        const {
            quotaLimit,
            spentQuota = 0,
            isOpen,
            styles,
            listName,
            modalPlaceholder = "",
            placeholder = "",
            isSaveDisabled,
        } = this.props;

        const { selectedKeys, errorMsg, isAutoCompleteLoading, itemsToRender, isLoading, showProTipBanner } =
            this.state;

        const companyGroups = this.getCompanyGroups(this.state.selectedKeys as DomainInfo[]);
        const companiesAmount = Object.keys(companyGroups).length;

        return (
            <WithTrack>
                {(track) => {
                    this.track = track;
                    return (
                        <BasicModal
                            isOpen={isOpen}
                            customStyles={merge({}, websiteModalDefaultStyle, styles)}
                            onRequestClose={this.onCloseClick}
                        >
                            <div>
                                <WebsiteModalHeaderContainer>
                                    <PrimaryBoxTitle>
                                        {listName === "" ? (
                                            <PlainTooltip
                                                tooltipContent={this.state.listName}
                                                placement="top"
                                                enabled={this.state.isListNameTruncated}
                                            >
                                                <AutosizeTooltipAnchor>
                                                    <AutosizeInput
                                                        maxLength={200}
                                                        value={this.state.listName}
                                                        placeholder={translate(modalPlaceholder)}
                                                        onChange={this.onListNameChange}
                                                        onBlur={this.onListNameBlur}
                                                        autoFocus
                                                        data-automation-textfield="list-settings-modal-title-text-field"
                                                    />
                                                </AutosizeTooltipAnchor>
                                            </PlainTooltip>
                                        ) : (
                                            translate("si.static-lists.list-modal.title")
                                        )}
                                    </PrimaryBoxTitle>
                                </WebsiteModalHeaderContainer>
                                <EditOpportuntiesModalTitle>
                                    <div>{translate("si.static-lists.list-modal.company.subtitle")}</div>
                                    <StyledWebsitesContainer
                                        data-automation="websites-count-text"
                                        data-automation-count={selectedKeys.length}
                                    >
                                        {`${companiesAmount} ${translate("si.common.companies")}`}
                                    </StyledWebsitesContainer>
                                </EditOpportuntiesModalTitle>
                                <WebsitesModalAutoComplete isError={!!errorMsg}>
                                    <Autocomplete
                                        isError={!!errorMsg}
                                        errorMsg={errorMsg as string}
                                        placeholder={translate(placeholder)}
                                        onBlur={() =>
                                            this.setState({
                                                itemsToRender: [],
                                            })
                                        }
                                        onPaste={this.onPaste}
                                        searchIcon="globe"
                                        loadingComponent={<DotsLoader />}
                                        isLoading={isAutoCompleteLoading}
                                        numOfSelectedItems={selectedKeys.length}
                                        maxResults={6}
                                        resetValueOnSelect
                                        debounce={400}
                                        getListItems={this.getData}
                                        autoFocus={listName !== ""}
                                    />
                                </WebsitesModalAutoComplete>
                                <SelectedKeysContainer isDropDownOpen={!!itemsToRender.length}>
                                    <WebsiteModalContainer>{this.renderCompanySelectedKeys()}</WebsiteModalContainer>
                                </SelectedKeysContainer>
                                {showProTipBanner ? (
                                    <StyledProTipBanner
                                        onCloseClick={this.onCloseProTipBanner}
                                        title={`${translate("workspace.sales.websites.modal.banner.title")}:`}
                                        subtitle={translate(
                                            "si.workspace.sales.websites.company.modal.banner.subtitle",
                                        )}
                                        iconImagePath={AssetsService.assetUrl(
                                            "/images/workspace/pro-tip-banner/pro-tip-banner-icon.png",
                                        )}
                                        iconImageHeight={64}
                                        iconImageWidth={64}
                                    />
                                ) : null}
                                {this.renderErrorLimitMessage()}
                                <Footer>
                                    {quotaLimit ? (
                                        <StyledQuotaContainer
                                            className={classNames({
                                                isLimit: quotaLimit - spentQuota < 10,
                                            })}
                                        >
                                            {`${translate("workspace.websitemodal.tracking.message")} ${spentQuota} / 
                                            ${quotaLimit} 
                                            ${translate("global.text.companies")}`}
                                        </StyledQuotaContainer>
                                    ) : null}
                                    <StyledButtonsGroup>
                                        <Button
                                            label={translate("global.cancel")}
                                            onClick={this.onCancelClick}
                                            dataAutomation="websites-modal-cancel-button"
                                            type="flat"
                                        />
                                        <PlainTooltip
                                            placement="top"
                                            enabled={!this.state?.listName}
                                            tooltipContent={translate("websites.modal.save-button.tooltip.text")}
                                        >
                                            <div>
                                                <Button
                                                    label={translate("global.save")}
                                                    onClick={this.onCompanySave}
                                                    isDisabled={
                                                        isSaveDisabled ||
                                                        selectedKeys.length === 0 ||
                                                        this.state.listName?.length === 0 ||
                                                        isLoading
                                                    }
                                                    isLoading={isLoading}
                                                    dataAutomation="websites-modal-save-button"
                                                    type="primary"
                                                />
                                            </div>
                                        </PlainTooltip>
                                    </StyledButtonsGroup>
                                </Footer>
                            </div>
                        </BasicModal>
                    );
                }}
            </WithTrack>
        );
    }

    handleSelectItem = (item: IAutoCompleteSiteItem) => {
        const { spentQuota = 0, quotaLimit = 0, inputLimit = 0 } = this.props;
        const { selectedKeys } = this.state;
        // one selected item + all already in the list + spent quota
        const isOverQuota = 1 + selectedKeys.length + spentQuota > quotaLimit;

        if (item.name.length > inputLimit) {
            this.setState({ isOverLimitError: true });
            return;
        }

        if (isOverQuota) {
            this.setState({ isManualError: true, isPasteError: false });
            return;
        }

        this.onKeySelected([
            { domain: item.name, icon: item.image || "", companyInfo: item.companyInfo as ICompanyItem },
        ]);
    };

    private onPaste = async (event: ClipboardEvent): Promise<void> => {
        this.setState({ isAutoCompleteLoading: true });
        const { spentQuota = 0, quotaLimit = 0, inputLimit = 0 } = this.props;
        const { selectedKeys } = this.state;

        const pastedKeys = event.clipboardData?.getData("text") ?? "";

        const domains: string[] = pastedKeys
            .replace(/[\t\r\n]/g, ",")
            .split(",")
            .map((domain: string) => domain.replace(/\s/g, ""))
            .filter(isValidURL);

        // valid domain is counted as a minimum 4 characters (spaces & web standards removed)
        const domainsWithPossibleDuplicates = domains
            .map(getDomain)
            .filter((domain = "") => domain.length > 3) as string[];

        const domainsWithDuplicatesRemoved = Array.from(new Set(domainsWithPossibleDuplicates));

        if (domainsWithDuplicatesRemoved.some((item) => item.length > inputLimit)) {
            this.setState({ isOverLimitError: true });
            return;
        }

        const isPasteOverQuota = domainsWithDuplicatesRemoved.length + selectedKeys.length + spentQuota > quotaLimit;

        if (isPasteOverQuota) {
            this.setState({ isPasteError: true, isManualError: false });
            return;
        }

        if (domainsWithPossibleDuplicates.length === 0) {
            this.setState({ isValidationError: true, isManualError: false });
            return;
        }

        const domainsWithCompanyInfo: IAutoCompleteSiteItem[] = await salesApiService.fetchAutocompleteCompanies({
            domains: domainsWithDuplicatesRemoved,
        });
        this.onKeySelected(
            domainsWithCompanyInfo.map(({ name, image, companyInfo }) => {
                return { domain: name, icon: image || "", companyInfo };
            }) as DomainInfo[],
        );

        this.setState({ isPasteError: false, isValidationError: false });
    };

    private renderCompanySelectedKeys = (): ReactElement[] => {
        const companyGroups = this.getCompanyGroups(this.state.selectedKeys as DomainInfo[]);
        return Object.values(companyGroups).map((item: CompanyGroup): ReactElement => {
            return (
                <CompanyListItem
                    key={item.companyInfo.companyName}
                    companyInfo={item.companyInfo}
                    domains={item.domains}
                    iconSrc={item.icon}
                    onClick={this.onCompanyRemove}
                />
            );
        });
    };

    private getData = async (query?: string): Promise<ReactElement[]> => {
        if (typeof query === "string" && query !== "") {
            this.setState({ isAutoCompleteLoading: true });

            const autoCompleteItems = await autoCompleteService.getAutoCompleteSuggestions({
                query,
                searchTypeStr: "WEBSITE",
                baseUrl: "/sales-api",
            });

            const itemsToRender = (autoCompleteItems as IAutoCompleteSiteItem[])
                .filter(
                    (autoCompleteItem) => !this.state.selectedKeys.find((key) => key.name === autoCompleteItem.name),
                )
                .map((item, index) => (
                    <ListItemWebsite
                        img={item.image || ""}
                        key={index}
                        onClick={() => this.handleSelectItem(item)}
                        text={item.name}
                    />
                ));
            this.setState({ isAutoCompleteLoading: false, itemsToRender });
            return itemsToRender;
        } else {
            return [];
        }
    };

    private onKeySelected = (domains: DomainInfo[]) => {
        const existingKeys = this.state.selectedKeys.map((key) => ({ ...key, Domain: key.domain }));

        const keysToAdd: DomainInfo[] = [];
        const keysToAddTrack: string[] = [];

        let errorMsg = null;
        let duplicatesNum = 0;

        domains.forEach((domain) => {
            if (
                existingKeys.some((key) => key.Domain === domain.domain) ||
                keysToAdd.some(({ domain: DomainToBeAdded }) => DomainToBeAdded === domain.domain)
            ) {
                duplicatesNum++;
            } else {
                keysToAdd.push(domain);
                keysToAddTrack.push(domain.domain);
            }
        });

        if (duplicatesNum === 1) {
            errorMsg = translate((this.props.errors ?? defaultErrors).DUPLICATE_KEY);
        } else if (duplicatesNum > 1) {
            errorMsg = translate((this.props.errors ?? defaultErrors).DUPLICATE_KEYS, {
                duplicatesNum: duplicatesNum.toString(),
            });
            errorMsg = translate((this.props.errors ?? defaultErrors).DUPLICATE_KEYS, {
                duplicatesNum: duplicatesNum.toString(),
            });
        }

        if (keysToAddTrack.length > 1) {
            this.track("Websites Modal", "Pasted domains", `${keysToAddTrack.length}`);
        } else {
            this.track("Websites Modal", "Add domain", `${keysToAddTrack[0]}`);
        }

        this.setState({
            errorMsg,
            isManualError: false,
            isPasteError: false,
            isValidationError: false,
            selectedKeys: this.state.selectedKeys.concat(keysToAdd),
        });
    };

    private onCompanyRemove = (name: string): void => {
        const selectedKeys = this.state.selectedKeys.filter(
            (item: DomainInfo) => item.companyInfo.companyName !== name,
        );
        this.track("Websites Modal", "remove company", name);
        this.setState({ selectedKeys });
    };

    private onCloseClick = () => {
        this.track("Websites Modal", "click", "close");
        this.props.onCloseClick();
        this.setState({ listName: this.props.listName ?? "" });
        this.modalReset();
    };

    private onCancelClick = () => {
        this.track("Websites Modal", "click", "cancel");
        this.props.onCancelClick?.();
        this.setState({ listName: this.props.listName ?? "" });
        this.modalReset();
    };

    private onCompanySave = async (): Promise<void> => {
        this.setState({ isLoading: true });

        const companyGroups = this.getCompanyGroups(this.state.selectedKeys as DomainInfo[]);
        const companiesAmount = Object.keys(companyGroups).length;

        const eventName = this.state.activeListId === LIST_ID_OVERVIEW ? "Add websites" : "Add existing leads";
        this.track("Websites Modal", "submit-ok", `${eventName}/${companiesAmount}`);

        const opportunities: readonly string[] = Object.values(companyGroups).flatMap(
            ({ domains }: CompanyGroup) => domains,
        );

        await this.props.onSave(opportunities, this.state.listName);
        this.modalReset();
    };

    private modalReset = () => {
        this.setState({
            errorMsg: null,
            isAutoCompleteLoading: false,
            country: this.props.country,
            selectedKeys: [] as IWebsiteItem[],
            isLoading: false,
            isPasteError: false,
            isValidationError: false,
            isManualError: false,
            isOverLimitError: false,
        });
    };

    private onListNameBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const input = event.target;
        if (!input) {
            return;
        }

        const isTruncated = input.scrollWidth > input.clientWidth;

        if (isTruncated !== this.state.isListNameTruncated) {
            this.setState({ isListNameTruncated: isTruncated });
        }
    };

    private onListNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (typeof this.props.onListNameChange === "function") {
            this.props.onListNameChange(event.target.value);
        }

        this.setState({
            listName: event.target.value,
        });
    };
}
